import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import Layout          from './components/Layout';
import { ConfirmProvider, ToastProvider, UIProvider } from './components/ui';
import { routeRegistry } from './config/routeRegistry';
import { StaffNotificationProvider } from './staff/NotificationHub';
import lazyWithReload from './utils/lazyWithReload';

const Login = lazyWithReload(() => import('./pages/Login'));
const CompanyHQ = lazyWithReload(() => import('./pages/CompanyHQ'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const Users = lazyWithReload(() => import('./pages/Users'));
const Cars = lazyWithReload(() => import('./pages/Cars'));
const Meets = lazyWithReload(() => import('./pages/Meets'));
const Spots = lazyWithReload(() => import('./pages/Spots'));
const Posts = lazyWithReload(() => import('./pages/Posts'));
const Vroomki = lazyWithReload(() => import('./pages/Vroomki'));
const SystemNews = lazyWithReload(() => import('./pages/SystemNews'));
const Routes_ = lazyWithReload(() => import('./pages/Routes'));
const Friendships = lazyWithReload(() => import('./pages/Friendships'));
const BugReports = lazyWithReload(() => import('./pages/support/SupportInbox'));
const BugReportRedirect = lazyWithReload(() => import('./pages/BugReportRedirect'));
const Conversations = lazyWithReload(() => import('./pages/Conversations'));
const Warnings = lazyWithReload(() => import('./pages/Warnings'));
const SpeedLimits = lazyWithReload(() => import('./pages/SpeedLimits'));
const Announcements = lazyWithReload(() => import('./pages/Announcements'));
const Campaigns = lazyWithReload(() => import('./pages/Campaigns'));
const Paywalls = lazyWithReload(() => import('./pages/Paywalls'));
const Achievements = lazyWithReload(() => import('./pages/Achievements'));
const AchievementDefs = lazyWithReload(() => import('./pages/AchievementDefs'));
const QuestTor = lazyWithReload(() => import('./pages/QuestTor'));
const BetaSignups = lazyWithReload(() => import('./pages/BetaSignups'));
const Grid = lazyWithReload(() => import('./pages/Grid'));
const CarContest = lazyWithReload(() => import('./pages/CarContest'));
const Polls = lazyWithReload(() => import('./pages/Polls'));
const Gifts = lazyWithReload(() => import('./pages/Gifts'));
const PremiumMembers = lazyWithReload(() => import('./pages/PremiumMembers'));
const MarketPricing = lazyWithReload(() => import('./pages/MarketPricing'));
const Partners = lazyWithReload(() => import('./pages/Partners'));
const StaffAccounts = lazyWithReload(() => import('./pages/StaffAccounts'));
const Referrals = lazyWithReload(() => import('./pages/Referrals'));
const UgcReports = lazyWithReload(() => import('./pages/UgcReports'));
const Animations = lazyWithReload(() => import('./pages/Animations'));
const Drops = lazyWithReload(() => import('./pages/Drops'));
const NitroShop = lazyWithReload(() => import('./pages/NitroShop'));
const NitroGrant = lazyWithReload(() => import('./pages/NitroGrant'));
const VehicleOrders = lazyWithReload(() => import('./pages/VehicleOrders'));
const FuelStationBrands = lazyWithReload(() => import('./pages/FuelStationBrands'));
const FuelPrices = lazyWithReload(() => import('./pages/FuelPrices'));
const Maintenance = lazyWithReload(() => import('./pages/Maintenance'));
const LandingContent = lazyWithReload(() => import('./pages/LandingContent'));
const BusinessAccounts = lazyWithReload(() => import('./pages/BusinessAccounts'));
const BusinessLocations = lazyWithReload(() => import('./pages/BusinessLocations'));
const BusinessAds = lazyWithReload(() => import('./pages/BusinessAds'));
const BusinessModeration = lazyWithReload(() => import('./pages/BusinessModeration'));
const BusinessCenter = lazyWithReload(() => import('./pages/BusinessCenter'));
const BusinessPricing = lazyWithReload(() => import('./pages/BusinessPricing'));
const BusinessOffers = lazyWithReload(() => import('./pages/BusinessOffers'));
const Analytics = lazyWithReload(() => import('./pages/Analytics'));
const NotificationSettings = lazyWithReload(() => import('./pages/NotificationSettings'));
const BusinessSupport = lazyWithReload(() => import('./pages/BusinessSupport'));
const ShopAdmin = lazyWithReload(() => import('./pages/ShopAdmin'));
const ShopAssortment = lazyWithReload(() => import('./pages/ShopAssortment'));
const UserInventoryAdmin = lazyWithReload(() => import('./pages/UserInventoryAdmin'));
const LegalAdmin = lazyWithReload(() => import('./pages/LegalAdmin'));
const ShopProductWizard = lazyWithReload(() => import('./pages/ShopProductWizard'));
const ShopCategories = lazyWithReload(() => import('./pages/ShopCategories'));
const CashFlow = lazyWithReload(() => import('./pages/CashFlow'));
const Seasons = lazyWithReload(() => import('./pages/Seasons'));
const SeasonWizard = lazyWithReload(() => import('./pages/SeasonWizard'));
const Economy = lazyWithReload(() => import('./pages/Economy'));
const Drive = lazyWithReload(() => import('./pages/Drive'));
const MarketingHQ = lazyWithReload(() => import('./pages/MarketingHQ'));
const MarketingEmails = lazyWithReload(() => import('./pages/MarketingEmails'));
const OwnerPlugins = lazyWithReload(() => import('./pages/OwnerPlugins'));

const routeComponents = {
  company: CompanyHQ,
  dashboard: Dashboard, analytics: Analytics, users: Users, cars: Cars, meets: Meets, spots: Spots, posts: Posts, vroomki: Vroomki,
  'system-news': SystemNews, routes: Routes_, friendships: Friendships, 'bug-reports': BugReports,
  conversations: Conversations, warnings: Warnings, 'speed-limits': SpeedLimits, announcements: Announcements, campaigns: Campaigns, paywalls: Paywalls,
  achievements: Achievements, 'achievement-defs': AchievementDefs, 'quest-tor': QuestTor, beta: BetaSignups,
  grid: Grid, 'car-contest': CarContest, polls: Polls, gifts: Gifts, 'premium-members': PremiumMembers, 'market-pricing': MarketPricing,
  partners: Partners, 'staff-accounts': StaffAccounts, referrals: Referrals, 'ugc-reports': UgcReports,
  animations: Animations, drops: Drops, 'nitro-shop': NitroShop, 'nitro-grant': NitroGrant,
  'vehicle-orders': VehicleOrders, 'fuel-station-brands': FuelStationBrands, 'fuel-prices': FuelPrices,
  maintenance: Maintenance, landing: LandingContent, 'business-accounts': BusinessAccounts,
  'business-locations': BusinessLocations, 'business-ads': BusinessAds, 'business-pricing': BusinessPricing,
  'business-offers': BusinessOffers, 'business-moderation': BusinessCenter,
  'business-support': BusinessSupport, 'notification-settings': NotificationSettings,
  'shop-dashboard': ShopAdmin, 'shop-catalog': ShopAdmin, 'shop-preorders': ShopAdmin, 'shop-orders': ShopAdmin, 'shop-promotions': ShopAdmin,
  'shop-designers': ShopAdmin, 'shop-finance': ShopAdmin, 'shop-settings': ShopAdmin, 'shop-documents': LegalAdmin,
  'shop-assortment': ShopAssortment, 'user-inventory': UserInventoryAdmin,
  legal: LegalAdmin,
  'cash-flow': CashFlow,
  seasons: Seasons,
  economy: Economy,
  drive: Drive,
  marketing: MarketingHQ,
  'marketing-emails': MarketingEmails,
  plugins: OwnerPlugins,
};

const isAuth = () => !!localStorage.getItem('adminToken');

function Protected({ children }) {
  return isAuth() ? children : <Navigate to="/login" replace />;
}

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <div className="cc-route-error"><div><span>BŁĄD MODUŁU</span><h1>Nie udało się wyświetlić tego ekranu</h1><p>{this.state.error.message || 'Wystąpił nieoczekiwany błąd interfejsu.'}</p><div><button className="cc-button cc-button--secondary" onClick={() => window.location.assign('/shop-assortment?tab=products')}>Wróć do produktów</button><button className="cc-button cc-button--primary" onClick={() => window.location.reload()}>Wczytaj ponownie</button></div></div></div>;
  }
}

export default function App() {
  return (
    <UIProvider><ToastProvider><ConfirmProvider><BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<div className="cc-route-loader" role="status" aria-live="polite"><span /><p>Ładowanie modułu…</p></div>}>
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/" element={<Protected><StaffNotificationProvider><Layout /></StaffNotificationProvider></Protected>}>
          {routeRegistry.flatMap((route) => {
            const Component = routeComponents[route.id];
            if (!Component) return [];
            if (route.path === '/') return [<Route key={route.id} index element={<Component />} />];
            return (route.routePatterns || [route.path.slice(1)]).map((path) => <Route key={`${route.id}-${path}`} path={path} element={<RouteErrorBoundary><Component /></RouteErrorBoundary>} />);
          })}
          <Route path="bug-report/:id" element={<BugReportRedirect />} />
          <Route path="nitro-shop" element={<Navigate to="/shop-assortment?tab=items" replace />} />
          <Route path="achievement-defs" element={<Navigate to="/economy?tab=points" replace />} />
          <Route path="quest-tor" element={<Navigate to="/economy?tab=tor" replace />} />
          <Route path="drops" element={<Navigate to="/economy?tab=drops" replace />} />
          <Route path="nitro-grant" element={<Navigate to="/economy?tab=nitro" replace />} />
          <Route path="shop-products/new" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-products/:id/edit" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-items/new" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-items/:id/edit" element={<RouteErrorBoundary><ShopProductWizard /></RouteErrorBoundary>} />
          <Route path="shop-categories" element={<RouteErrorBoundary><ShopCategories /></RouteErrorBoundary>} />
          <Route path="seasons/new" element={<RouteErrorBoundary><SeasonWizard /></RouteErrorBoundary>} />
          <Route path="seasons/:seasonId" element={<RouteErrorBoundary><Seasons /></RouteErrorBoundary>} />
          <Route path="plugins/:id" element={<RouteErrorBoundary><OwnerPlugins /></RouteErrorBoundary>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter></ConfirmProvider></ToastProvider></UIProvider>
  );
}
